import { useQuery } from "@tanstack/react-query";
import { GET_QA_PROVISION_JOB } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { QA_JOB_FINISHED, type QaProvisionJob } from "@/types/qaProvision";

/** Matches the cadence used for cached reports (see useCachedReport). */
const POLL_INTERVAL_MS = 2000;

/**
 * Consecutive failed fetches before giving up. A blip recovers well inside this;
 * a backend that is simply unreachable would otherwise be polled forever.
 */
const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * How long a non-terminal job may go without a progress write before we treat it
 * as abandoned. The worker writes at least every second while it has work, so
 * silence this long means it is gone — it hit its 15-minute ceiling mid-batch, or
 * died before it could record a failure. Nothing will ever move the record to a
 * terminal status, so polling it forever is pointless.
 */
const STALE_AFTER_MS = 5 * 60 * 1000;

const isAbandoned = (job: QaProvisionJob | undefined): boolean => {
  if (!job || QA_JOB_FINISHED.includes(job.status)) return false;
  const updatedAt = Date.parse(job.updatedAt ?? "");
  if (Number.isNaN(updatedAt)) return false;
  return Date.now() - updatedAt > STALE_AFTER_MS;
};

/**
 * Poll a provisioning job until it finishes. Provisioning a batch takes minutes
 * (a rostering call plus one mutation per manifest line, per climber), so the
 * page follows the job record rather than waiting on a request.
 */
export const useQaProvisionJob = (jobId: string | null) => {
  const query = useQuery<QaProvisionJob>({
    queryKey: ["qaProvisionJob", jobId],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        getQaProvisionJob: QaProvisionJob | null;
      }>(GET_QA_PROVISION_JOB, { jobId });
      if (!response.getQaProvisionJob) {
        throw new Error(`Job ${jobId} not found.`);
      }
      return response.getQaProvisionJob;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && QA_JOB_FINISHED.includes(status)) return false;
      // React Query keeps the interval running through errors, so both of these
      // have to stop it explicitly.
      if (query.state.fetchFailureCount >= MAX_CONSECUTIVE_FAILURES)
        return false;
      if (isAbandoned(query.state.data)) return false;
      return POLL_INTERVAL_MS;
    },
    // Live progress: never serve a cached job. The job carries the created
    // climbers' passwords, so keep it out of the localStorage-persisted cache
    // (see queryClient.ts) and drop it as soon as the page stops watching.
    staleTime: 0,
    gcTime: 0,
    meta: { persist: false },
  });

  return {
    job: query.data ?? null,
    isPolling: query.isFetching,
    error: query.error as Error | null,
    /** The job stopped reporting; treat it as over, outcome unknown. */
    isAbandoned: isAbandoned(query.data),
  };
};
